import * as React from "react";
import { Button } from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import {
    ArrowLeftRegular,
    ArrowRightRegular,
    Settings20Regular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { getFinnishHoliday } from "../../utils/finnishHolidays";

interface IWeektableNavigationProps {
    selectedDays: number[];
    handleWeekChange: (week: Date[]) => void;
    handlePrev: () => void;
    handleNext: () => void;
    handleGoToday: () => void;
    getThisWeek: (date: Date) => Date[];
    onOpenWeekOptions?: () => void;
}

export default function WeektableNavigation(props: IWeektableNavigationProps) {
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(
        props.selectedDays[0] ? new Date(props.selectedDays[1]) : new Date(),
    );

    React.useEffect(() => {
        setSelectedDate(
            props.selectedDays[1]
                ? new Date(props.selectedDays[1])
                : new Date(),
        );
    }, [props.selectedDays]);

    // Check if the selected date is a holiday
    const holiday = selectedDate ? getFinnishHoliday(selectedDate) : null;

    return (
        <div className="w-full max-w-full overflow-x-auto overflow-y-hidden mb-2 sm:mb-2">
            <div className="flex items-center justify-center gap-1 sm:gap-2 flex-nowrap px-1">
                <Button
                    appearance="transparent"
                    size="medium"
                    key="previousButton"
                    icon={<ArrowLeftRegular />}
                    id="1"
                    onClick={props.handlePrev}
                    aria-label="Previous week"
                    className="min-h-[36px] min-w-[36px] sm:min-h-[44px] sm:min-w-[44px] touch-manipulation p-0 shrink-0"
                />
                <div className="flex items-center gap-1 shrink-0">
                    <label htmlFor="weektable-date-input" className="sr-only">
                        Select date
                    </label>
                    <DatePicker
                        id="weektable-date-input"
                        value={selectedDate ?? null}
                        firstDayOfWeek={1}
                        onSelectDate={(date) => {
                            if (!date) return;
                            // Use the calendar selection to determine the week,
                            // but always normalize the visible value to the week's Monday.
                            const week = props.getThisWeek(date);
                            const monday = week[1] ? new Date(week[1]) : date;
                            setSelectedDate(monday);
                            props.handleWeekChange(week);
                        }}
                        placeholder="Select week"
                        today={new Date()}
                        allowTextInput={false}
                        showGoToToday
                        formatDate={(d) =>
                            d
                                ? `${
                                    d.toLocaleDateString("en-FI", {
                                        weekday: "short",
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })
                                }`
                                : ""}
                        className="min-h-[36px] sm:min-h-[36px] max-w-[150px] sm:max-w-[260px]"
                    />
                </div>
                <Button
                    appearance="transparent"
                    size="medium"
                    key="nextButton"
                    icon={<ArrowRightRegular />}
                    id="3"
                    onClick={props.handleNext}
                    aria-label="Next week"
                    className="min-h-[36px] min-w-[36px] sm:min-h-[44px] sm:min-w-[44px] touch-manipulation p-0 shrink-0"
                />
                <Button
                    size="medium"
                    key="goToday"
                    onClick={props.handleGoToday}
                    className="min-h-[36px] px-2.5 sm:px-3 touch-manipulation text-xs sm:text-sm font-semibold shrink-0"
                >
                    TODAY
                </Button>
                {props.onOpenWeekOptions && (
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<Settings20Regular />}
                        aria-label="Week options"
                        onClick={props.onOpenWeekOptions}
                        className="min-h-[32px] min-w-[32px] shrink-0"
                    />
                )}
            </div>
        </div>
    );
}
